const DB_NAME = "grabi";
const DB_VERSION = 1;

export type StoreName = "meta" | "takes";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta");
      if (!db.objectStoreNames.contains("takes")) db.createObjectStore("takes", { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      // One transient failure must not disable persistence for the page.
      dbPromise = null;
      reject(request.error ?? new Error("indexedDB failed"));
    };
    request.onblocked = () => {
      dbPromise = null;
      reject(new Error("indexedDB is blocked by another tab"));
    };
  });
  return dbPromise;
}

function tx<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T> | null,
): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const transaction = db.transaction(store, mode);
        const objectStore = transaction.objectStore(store);
        let request: IDBRequest<T> | null = null;
        try {
          request = run(objectStore);
        } catch (error) {
          reject(error);
          return;
        }
        transaction.oncomplete = () => resolve(request?.result);
        transaction.onerror = () => reject(transaction.error ?? new Error("indexedDB transaction failed"));
        transaction.onabort = () => reject(transaction.error ?? new Error("indexedDB aborted"));
      }),
  );
}

export async function idbGet<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  return tx<T>(store, "readonly", (s) => s.get(key) as IDBRequest<T>);
}

export async function idbPut(store: StoreName, key: IDBValidKey, value: unknown): Promise<void> {
  await tx(store, "readwrite", (s) => s.put(value, key));
}

export async function idbPutRecord(store: StoreName, value: unknown): Promise<void> {
  await tx(store, "readwrite", (s) => s.put(value));
}

export async function idbDelete(store: StoreName, key: IDBValidKey): Promise<void> {
  await tx(store, "readwrite", (s) => s.delete(key));
}

export async function idbAll<T>(store: StoreName): Promise<T[]> {
  const result = await tx<T[]>(store, "readonly", (s) => s.getAll() as IDBRequest<T[]>);
  return result ?? [];
}
